<script setup lang="ts">
import { computed, useDevice } from '#imports';

type Props = {
    srcImg?: string;
    srcAnim?: string;
    name?: string;
    isLoading?: boolean;
};

const { isMobileOrTablet } = useDevice();
const { srcImg, srcAnim, name } = defineProps<Props>();
const isVideo = computed(() => srcImg && (/\.(mp4|webm|ogg)$/i).test(srcImg));
</script>

<template>
  <div class="media-viewer-container">
    <LoaderSkeleton v-if="isLoading"
                    class="token-img" />
    <video v-else-if="!!isVideo"
           :autoplay="!isMobileOrTablet"
           class="token-img width-full height-full"
           controls
           loop
           muted
    >
      <source :src="srcImg">
    </video>
    <template v-else>
      <ImgMain :src="srcImg"
               :alt="name"
               :animated-src="srcAnim"
               draggable="false"
               :content-loading="isLoading"
               decoding="async"
               loading="lazy"
               class="token-img"
               sizes="320px xs:587px sm:588px md:588px lg:600px xl:672px xxl:672px"
      />

      <NuxtImg :src="srcImg"
               :alt="name"
               draggable="false"
               :placeholder="[50, 25, 75, 5]"
               quality="50"
               format="webp"
               :width="640"
               :height="640"
               :style="{
                 position: 'absolute',
                 top: 0,
                 left: 0,
                 width: '100%',
                 height: '100%',
                 maxHeight: '660px',
                 maxWidth: '660px',
                 zIndex: 0,
                 filter: 'blur(50px) opacity(0.4)',
               }"
      />
    </template>
  </div>
</template>

<style scoped lang="scss">
.media-viewer-container {
  position: relative;
  grid-area: img;
  width: 100%;
  max-width: 640px;
  max-height: 640px;

  &.token-img {
    position: relative;
    z-index: 2;
    border: var(--image-paranja-stroke, 1px) solid var(--border-paranja, rgb(255 255 255 / 20%));
    border-radius: 2px;
    aspect-ratio: 1 / 1;

    video {
      object-fit: cover;
    }

    img {
      transform: scale(1.05);
    }
  }

  @include tablet-768 {
    justify-self: center;
    max-width: 560px;
    max-height: 560px;
  }
}

</style>
